import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from "@stripe/react-stripe-js";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { WeeklyIdeasOptIn, useIdeaMailOptIn } from "@/components/WeeklyIdeasOptIn";
import { Loader2, Lock, ArrowRight, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { trackEvent, planItem, getAttribution, gaClientId } from "@/components/Analytics";
import { getCookieConsent } from "@/components/CookieConsent";
import { PLAN_CATALOG } from "@/components/PlanSelection";

const TEAL = "#1dddbf";

// Single source of truth: derive the checkout summary from the pricing catalog so the
// order box always lists the FULL feature set of the selected plan (consistent with /priser).
const planMeta = (plan) => {
  const c = PLAN_CATALOG[plan] || PLAN_CATALOG.pro;
  return { name: `Aicom ${c.name}`, monthly: c.price, bullets: c.features, credits: c.credits };
};

function CheckoutForm({ subscriptionId, meta, planKey, email, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isBusiness, setIsBusiness] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [cvr, setCvr] = useState("");
  const ideaMail = useIdeaMailOptIn();

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (isBusiness && (!businessName.trim() || !cvr.trim())) {
      toast.error("Udfyld virksomhedsnavn og CVR");
      return;
    }
    if (!accepted) {
      toast.error("Du skal acceptere handelsbetingelserne for at fortsætte");
      return;
    }
    const addressEl = elements.getElement("address");
    let billingDetails;
    if (addressEl) {
      const { complete, value } = await addressEl.getValue();
      if (!complete) { toast.error("Udfyld navn og faktureringsadresse"); return; }
      billingDetails = { name: value.name, address: value.address, email };
    }
    setSubmitting(true);
    await ideaMail.commit();
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard?welcome=1`,
        payment_method_data: billingDetails ? { billing_details: billingDetails } : undefined,
      },
      redirect: "if_required",
    });
    if (error) {
      toast.error(error.message || "Betalingen kunne ikke gennemføres");
      setSubmitting(false);
      return;
    }
    if (setupIntent && setupIntent.status === "succeeded") {
      try {
        await api.post("/billing/confirm-trial", {
          setup_intent_id: setupIntent.id,
          business: isBusiness ? { name: businessName.trim(), cvr: cvr.trim() } : null,
        });
        // trial_started is emitted server-side from the confirmed Stripe subscription
        // (customer.subscription.created), so it reflects a real, verified action.
        onSuccess();
      } catch (err) {
        toast.error(apiError(err));
        setSubmitting(false);
      }
    } else {
      // Redirect flow in progress (e.g. 3D Secure) — Stripe will navigate away.
      setSubmitting(false);
    }
  };

  const inputCls = "mt-1 w-full h-11 rounded-lg bg-secondary/40 border border-border px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[#1dddbf]";

  return (
    <form onSubmit={submit} data-testid="wizard-payment-form">
      <PaymentElement options={{ layout: "tabs", fields: { billingDetails: { name: "never", address: "never", email: "never" } } }} />
      <div className="mt-5">
        <AddressElement options={{ mode: "billing", defaultValues: { address: { country: "DK" } }, fields: { phone: "never" } }} />
      </div>

      <label className="flex gap-3 items-start cursor-pointer mt-6" data-testid="wizard-business-toggle">
        <Checkbox checked={isBusiness} onCheckedChange={(v) => setIsBusiness(v === true)} className="mt-0.5" data-testid="wizard-business-checkbox" />
        <span className="text-sm">
          <span className="text-foreground">Købet er til en virksomhed</span>
          <span className="block text-muted-foreground text-xs mt-0.5">Tilføj virksomhedsnavn og CVR til fakturaen</span>
        </span>
      </label>

      {isBusiness && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="wizard-business-fields">
          <div>
            <label className="text-sm text-muted-foreground">Virksomhedsnavn</label>
            <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className={inputCls} placeholder="Virksomhedens navn" data-testid="wizard-business-name" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">CVR-nummer</label>
            <input value={cvr} onChange={(e) => setCvr(e.target.value)} inputMode="numeric" className={inputCls} placeholder="12345678" data-testid="wizard-business-cvr" />
          </div>
        </div>
      )}

      <label className="flex gap-3 items-start cursor-pointer mt-6" data-testid="wizard-terms-consent">
        <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(v === true)} className="mt-0.5" data-testid="wizard-terms-checkbox" />
        <span className="text-sm text-muted-foreground leading-relaxed">
          Jeg har læst og accepterer{" "}
          <Link to="/handelsbetingelser" target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-4 hover:no-underline" data-testid="wizard-terms-link">
            handelsbetingelserne
          </Link>.
        </span>
      </label>

      {ideaMail.show && <WeeklyIdeasOptIn checked={ideaMail.checked} onChange={ideaMail.setChecked} className="mt-4" />}

      <Button
        type="submit"
        className="w-full h-11 mt-6 gap-2 font-medium"
        style={{ backgroundColor: TEAL, color: "#04211c" }}
        disabled={!stripe || submitting || !accepted}
        data-testid="wizard-start-trial-submit"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        Start 30 dages gratis prøveperiode <ArrowRight className="h-4 w-4" />
      </Button>
      <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
        <Lock className="h-3 w-3" /> Sikker betaling via Stripe
      </p>
    </form>
  );
}

export function WizardCheckout({ plan = "pro", onSuccess }) {
  const { user } = useAuth();
  const startedRef = useRef(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [err, setErr] = useState(null);
  const meta = planMeta(plan);

  useEffect(() => {
    if (startedRef.current) return; // guard against StrictMode double-invoke creating 2 subscriptions
    startedRef.current = true;
    (async () => {
      // Link first-party attribution + GA4 client_id to the account BEFORE Stripe,
      // so server-side conversions can join the web session. Only with consent; no PII.
      try {
        const consent = getCookieConsent() || {};
        const attr = getAttribution();
        await api.post("/attribution", {
          ga_client_id: consent.statistics ? gaClientId() : null,
          statistics: !!consent.statistics,
          marketing: !!consent.marketing,
          utm_source: attr.utm_source, utm_medium: attr.utm_medium, utm_campaign: attr.utm_campaign,
          utm_content: attr.utm_content, utm_term: attr.utm_term,
          landing_page: attr.landing_page, referrer: attr.referrer,
        });
      } catch (e) { /* attribution is best-effort; never block checkout */ }
      trackEvent("begin_checkout", { currency: "DKK", value: planItem(plan).price, items: [planItem(plan)] });
      try {
        const { data } = await api.post("/billing/trial-subscription", { plan });
        if (!data.publishable_key) {
          setErr("Betaling er ikke konfigureret endnu.");
          return;
        }
        setStripePromise(loadStripe(data.publishable_key));
        setClientSecret(data.client_secret);
        setSubscriptionId(data.setup_intent_id);
      } catch (e) {
        setErr(apiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            locale: "da",
            appearance: {
              theme: "night",
              variables: {
                colorPrimary: TEAL,
                colorBackground: "#0b0f0e",
                borderRadius: "10px",
                fontFamily: "inherit",
              },
            },
          }
        : null,
    [clientSecret]
  );

  return (
    <div data-testid="wizard-checkout">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Start din prøveperiode</p>
      <h1 className="font-display text-3xl font-medium tracking-tight">Prøv {meta.name} gratis</h1>
      <p className="text-muted-foreground mt-2 text-sm">30 dage med alle funktioner. Ingen binding.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <p className="text-sm font-medium mb-4">Betalingsoplysninger</p>
          {err ? (
            <div className="rounded-xl border border-border bg-secondary/30 p-4 text-sm text-muted-foreground" data-testid="wizard-checkout-error">
              {err}
            </div>
          ) : !clientSecret || !stripePromise || !options ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-8" data-testid="wizard-checkout-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Indlæser sikker betaling…
            </div>
          ) : (
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm subscriptionId={subscriptionId} meta={meta} planKey={plan} email={user?.email} onSuccess={onSuccess} />
            </Elements>
          )}
        </div>

        <aside className="lg:col-span-2 order-first lg:order-none">
          <div className="rounded-2xl border border-border bg-secondary/30 p-6" data-testid="wizard-checkout-summary">
            <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: TEAL }}>
              30 dages gratis prøveperiode
            </p>
            <p className="font-display text-xl font-medium">{meta.name}</p>
            {meta.credits && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="wizard-checkout-credits">{meta.credits}</p>
            )}
            <ul className="mt-4 space-y-2">
              {meta.bullets.map((b) => (
                <li key={b} className="text-sm text-muted-foreground flex gap-2">
                  <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: TEAL }} /> {b}
                </li>
              ))}
            </ul>
            <div className="my-5 h-px bg-border" />
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">I dag</span>
              <span className="font-display text-2xl font-medium">0 kr.</span>
            </div>
            <div className="flex items-baseline justify-between mt-2">
              <span className="text-sm text-muted-foreground">Efter 30 dage</span>
              <span className="text-sm text-foreground">{meta.monthly}/md. inkl. moms</span>
            </div>
            <p className="text-xs text-muted-foreground mt-5 leading-relaxed flex gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" style={{ color: TEAL }} />
              Der trækkes ikke betaling nu. Når prøveperioden udløber, fornyes abonnementet automatisk til {meta.monthly}/md.,
              medmindre du opsiger inden da.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
